import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkles, AlertTriangle, Loader2, Send, Bot } from "lucide-react";

export default function AIAssistant() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const run = async () => {
    setLoading(true);
    try { const { data } = await api.post("/ai/insights"); setData(data); }
    catch { toast.error("AI engine unavailable"); } finally { setLoading(false); }
  };
  useEffect(() => { run(); }, []);

  const send = async () => {
    if (!msg.trim()) return;
    const q = msg; setMsg(""); setChat((c) => [...c, { role: "user", text: q }]); setSending(true);
    try { const { data } = await api.post("/ai/chat", { message: q }); setChat((c) => [...c, { role: "ai", text: data.reply }]); }
    catch { setChat((c) => [...c, { role: "ai", text: "Sorry, I couldn't process that." }]); } finally { setSending(false); }
  };

  return (
    <div data-testid="ai-assistant-page">
      <div className="flex items-end justify-between mb-8">
        <div><h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">AI Engine</h4><h1 className="font-heading text-4xl font-black tracking-tighter uppercase">AI Assistant</h1></div>
        <Button onClick={run} disabled={loading} data-testid="refresh-insights-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" />Re-analyze</>}</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-primary/30 rounded-lg p-6" data-testid="ai-insights">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="h-5 w-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Catalog Insights</h3></div>
          {loading ? <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" /></div>
            : <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{data?.insights}</div>}
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="h-5 w-5 text-amber-400" /><h3 className="font-heading text-lg font-semibold">Detected Issues</h3></div>
          {data?.detected_issues?.length ? (
            <div className="space-y-2">{data.detected_issues.map((i) => (
              <button key={i.song_id} data-testid={`issue-${i.song_id}`} onClick={() => navigate(`/songs/${i.song_id}`)} className="w-full text-left bg-secondary/50 border border-border rounded-md px-4 py-3 hover:border-amber-400/40 transition-colors">
                <div className="font-semibold text-sm">{i.title}</div>
                <div className="text-xs text-amber-400/90 mt-1">{i.problems.join(" · ")}</div>
              </button>))}</div>
          ) : <p className="text-sm text-zinc-500 py-6">No issues detected. Your catalog is healthy. ✦</p>}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6 mt-6">
        <div className="flex items-center gap-2 mb-4"><Bot className="h-5 w-5 text-cyan-300" /><h3 className="font-heading text-lg font-semibold">Ask the Assistant</h3></div>
        <div className="space-y-3 mb-4 max-h-72 overflow-y-auto" data-testid="ai-chat-log">
          {chat.length === 0 && <p className="text-sm text-zinc-500">Ask anything about your publishing, royalties, rights or deals.</p>}
          {chat.map((m, i) => (
            <div key={i} className={`text-sm rounded-md px-4 py-2.5 ${m.role === "user" ? "bg-primary/10 border border-primary/30 ml-8" : "bg-secondary border border-border mr-8 whitespace-pre-wrap"}`}>{m.text}</div>
          ))}
          {sending && <div className="bg-secondary border border-border rounded-md px-4 py-2.5 mr-8"><Loader2 className="h-4 w-4 animate-spin text-cyan-300" /></div>}
        </div>
        <div className="flex gap-2">
          <Input data-testid="ai-chat-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="e.g. How do I register missing ISWCs?" className="bg-secondary border-border" />
          <Button onClick={send} disabled={sending} data-testid="ai-chat-send" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
